import { useMutation, useQueryClient } from "@tanstack/react-query";
import { testCornerstone } from "@/api/cornerstone";
import { useToasts } from "@/hooks/useToast";
import type { ApiError, CornerstoneStatus } from "@/types";

interface Props {
  disabled: boolean;
}

/**
 * Test-connection card for Cornerstone — tries the stored API key against
 * Cornerstone Hub and reports the result.
 */
export function CornerstoneConnectionDiagnostics({ disabled }: Props) {
  const qc = useQueryClient();
  const push = useToasts((s) => s.push);

  const test = useMutation<CornerstoneStatus, ApiError>({
    mutationFn: testCornerstone,
    onSuccess: (data) => {
      qc.setQueryData(["integrations", "cornerstone"], data);
      if (data.status === "active") {
        push("Cornerstone Hub is reachable — API key is valid.", "success");
      } else {
        push("Cornerstone Hub returned an error. Check the key and Hub URL.", "error");
      }
    },
    onError: (err) => {
      push(err.message ?? "Connection test failed", "error");
      qc.invalidateQueries({ queryKey: ["integrations", "cornerstone"] });
    },
  });

  return (
    <div className="card" aria-labelledby="cs-diag-title">
      <h3 id="cs-diag-title" style={{ margin: "0 0 6px" }}>
        Connection diagnostics
      </h3>
      <p className="muted" style={{ margin: "0 0 12px" }}>
        Validates the stored API key against Cornerstone Hub right now.
      </p>
      <button
        id="cs-test-btn"
        className="btn btn-sm"
        disabled={disabled || test.isPending}
        onClick={() => test.mutate()}
      >
        {test.isPending ? "Testing…" : "Test connection"}
      </button>
      {test.isError && (
        <div className="callout error" role="alert" style={{ marginTop: 12 }}>
          {test.error.message}
        </div>
      )}
    </div>
  );
}
